import FeatureService from '../feature/FeatureService';
import Features from '../../const/Features';
import _ from 'lodash';
import Country from '../../const/Country';
import LocalStorage from '../../const/LocalStorage';
import { Currency } from '../../const/CurrencyType';

export default {
  saveBooking
};

const POST_BOOKING_V1 = '/checkout/api/v1/booking/';
const POST_BOOKING_V2 = '/checkout/api/v2/booking/';

function saveBooking(bookingData, featureAdyenEncryptedEnabled, isCallcenterMode) {
  return new Promise((resolve, reject) => {
    FeatureService.isFeatureEnabled(Features.AMADEUS_WITH_MASTER_PRICER).then(isEnabled => {
      let status;
      fetch(isEnabled ? POST_BOOKING_V2 : POST_BOOKING_V1, {
        method: 'POST',
        body: JSON.stringify(
          assembleBookingRequest(bookingData, isEnabled, featureAdyenEncryptedEnabled)
        ),
        headers: assemblerHeaders(bookingData.principalContactInfo, isCallcenterMode),
        credentials: 'same-origin'
      })
        .then(res => {
          status = res.status;
          return res.json();
        })
        .then(body => {
          resolve({
            status,
            body
          });
        })
        .catch(error => {
          resolve({
            status: status || error.status,
            body: error
          });
        });
    });
  });
}

function assembleBookingRequest(bookingData, isFeatureEnabled, featureAdyenEncryptedEnabled) {
  const {
    principalContactInfo,
    checkoutInfo,
    passengerInfo,
    flightsFilter,
    quotationJourney,
    isPrincipalContact = true
  } = bookingData;

  const { refundRule } = quotationJourney;

  const bookingRequest = {
    passengers: assemblePassengers(passengerInfo, isPrincipalContact),
    principalContact: assemblePrincipalContact(principalContactInfo),
    payment: assemblePayment(checkoutInfo, featureAdyenEncryptedEnabled),
    cart: {
      items: [assembleCartItem(flightsFilter, quotationJourney, isFeatureEnabled)]
    },
    refundRule: assembleRefundRule(quotationJourney, refundRule),
    agreeCancellationTerms: quotationJourney.agreeCancellationTerms
  };

  bookingRequest.cart.total = assembleCartTotal(bookingRequest);
  bookingRequest.installment = checkoutInfo.installments ? assembleInstallments(checkoutInfo.installments, bookingRequest.cart.total): null;
  return bookingRequest;
}

function assembleInstallments(installment) {
  return {
      totalAmount:  installment.total,
      installmentValue: installment.installments[0],
      numberOfInstallments: installment.installmentNumber,
      interestsInstallment: installment.interestsInstallment,
    };
  }

function assemblePassengers(passengerInfo, isPrincipalContact) {
  const passengers = [];

  const infants = passengerInfo.filter(passenger => passenger.passengerType === 'IN');
  let index = 0;

  let principalContact = isPrincipalContact;
  passengerInfo.forEach(passenger => {
    if (passenger.passengerType !== 'IN') {
      const passengerObject = assemblePassenger(passenger, principalContact);
      principalContact = false;

      if (passenger.passengerType === 'ADT') {
        let infantWith = obtainInfantWith(index, infants);
        if (infantWith) {
          let infantObject = assemblePassenger(infantWith, false);
          delete infantObject.email;
          passengerObject.infant = infantObject;
        }
        index++;
      }
      passengers.push(passengerObject);
    }
  });
  return passengers;
}

function obtainFareAndFee(departureFlight, returnFlight, departureCategory, returnCategory) {
  let fare = null;
  let fee = null;

  if (returnFlight) {
    fare = returnFlight.fare[returnCategory];
    fee = returnFlight.fee[returnCategory];
  } else {
    let recommendationCategory = departureFlight.recommendations.filter(
      recommendation =>
        recommendation.departureCategory.toUpperCase() === departureCategory.toUpperCase()
    );
    fare = recommendationCategory[0].fare;
    fee = recommendationCategory[0].fee;
  }
  return [fare, fee];
}

function obtainValidateCarrier(departureFlight, returnFlight, departureCategory, returnCategory) {
  const recommendations = departureFlight.recommendations.filter(
    recommendation =>
      recommendation.departureCategory.toUpperCase() === departureCategory.toUpperCase()
  );

  let validateCarrier;
  if (returnFlight) {
    validateCarrier = _.chain(recommendations[0])
      .get('returnFlights')
      .find(flight => flight.id === returnFlight.id)
      .get('categories')
      .find(category => category.returnCategory.toUpperCase() === returnCategory.toUpperCase())
      .get('validateCompany')
      .value();
  } else {
    validateCarrier = recommendations[0].validateCompany;
  }

  return validateCarrier;
}

function assemblePrincipalContact(principalContactInfo) {
  const principalContact = {
    address: null,
    document: null,
    firstName: principalContactInfo.firstName,
    lastName: principalContactInfo.lastName,
    gender: principalContactInfo.gender,
    birthDate: principalContactInfo.birthDate,
    email: principalContactInfo.email,
    mobile: principalContactInfo.mobile,
    phone: principalContactInfo.phone
  };

  if (principalContactInfo.address) {
    principalContact.address = {
      apartmentNumber: principalContactInfo.address.apartmentNumber,
      city: principalContactInfo.address.city,
      country: principalContactInfo.address.country,
      house: principalContactInfo.address.house,
      neighbourhood: principalContactInfo.address.neighbourhood,
      postalCode: principalContactInfo.address.postalCode,
      region: principalContactInfo.address.region,
      street: principalContactInfo.address.street,
      streetPrefix: principalContactInfo.address.streetPrefix
    };
  }

  if (principalContactInfo.document) {
    principalContact.document = {
      number: principalContactInfo.document.number,
      type: principalContactInfo.document.type
    };
  }

  return principalContact;
}

function assemblePaymentEncrypted(checkoutInfo) {
  return {
    items: [
      {
        vendorCode: checkoutInfo.cardType.toUpperCase(),
        cardEncrypted: checkoutInfo.cardEncrypted
      }
    ]
  };
}

function assemblePaymentNotEncrypted(checkoutInfo) {
  return {
    items: [
      {
        vendorCode: checkoutInfo.cardType.toUpperCase(),
        cardNumber: checkoutInfo.cardNumber.replace(/\s/g, ''),
        securityId: checkoutInfo.securityCode,
        expiryMonth: checkoutInfo.month.replace(/\b(\d{1})\b/g, '0$1'),
        expiryYear: checkoutInfo.year,
        ccHolderName: checkoutInfo.name
      }
    ]
  };
}

function assemblePayment(checkoutInfo, featureAdyenEncryptedEnabled) {
  let payment = null;
  if (featureAdyenEncryptedEnabled) {
    payment = assemblePaymentEncrypted(checkoutInfo);
  } else {
    payment = assemblePaymentNotEncrypted(checkoutInfo);
  }
  return payment;
}


function obtainPassengerType(passengerType) {
  let type = null;
  if (passengerType === 'ADT') {
    type = 'ADULT';
  } else if (passengerType === 'CH') {
    type = 'CHILD';
  } else if (passengerType === 'IN') {
    type = 'INFANT';
  }
  return type;
}

function obtainPassengerGender(passengerGender) {
  let gender = null;

  if (passengerGender === 'Masculino') {
    gender = 'MALE';
  } else if (passengerGender === 'Feminino') {
    gender = 'FEMALE';
  }

  return gender;
}


function obtainPassengerCountry(countrySelected) {
  let retorno = null;
  Object.keys(Country).forEach(abrev => {
    if (Country[abrev] === countrySelected) {
      retorno = abrev;
    }
  }
  );
  return retorno === "default" ? null : retorno;
}

function obtainPassengePassport(passport) {
  return passport === "" ? null : passport;
}

function obtainPassengeExpirationDatePassport(expirationDatePassport) {
  return expirationDatePassport === "" ? null : expirationDatePassport;
}


function assemblePassenger(passengerData, principalContact) {
  return {
    principalContact: principalContact,
    name: passengerData.name,
    lastName: passengerData.lastName,
    gender: obtainPassengerGender(passengerData.gender),
    birthDate: passengerData.age,
    email: passengerData.email,
    passengerType: obtainPassengerType(passengerData.passengerType),
    passport: obtainPassengePassport(passengerData.passport),
    nationality: obtainPassengerCountry(passengerData.nationality),
    countryPassport: obtainPassengerCountry(passengerData.countryPassport),
    expirationDatePassport: obtainPassengeExpirationDatePassport(passengerData.expirationDatePassport)
  };
}

function obtainInfantWith(index, infants) {
  let infant = infants.filter(infant => {
    let adultsIndexes = infant.travellingWithValues;
    return adultsIndexes.indexOf(infant.travellingWith) === index;
  });
  return infant[0];
}

function assembleCartItem(flightsFilter, quotationJourney, isFeatureEnabled) {
  const originCode = flightsFilter.flightOrigin.searchCode;
  const destinationCode = flightsFilter.flightDestiny.searchCode;

  const departureFlight = quotationJourney.departureFlight;
  const returnFlight = quotationJourney.returnFlight;

  let departureFare = null;
  let returnFare = null;

  const [fare, fee] = obtainFareAndFee(
    departureFlight.flight,
    returnFlight ? returnFlight.flight : null,
    departureFlight.category,
    returnFlight ? returnFlight.category : null
  );

  if (fare && returnFlight) {
    departureFare = fare['departure'];
    returnFare = fare['return'];
  } else {
    departureFare = fare;
  }

  const item = {
    origin: departureFlight.flight.originAirport,
    originAirportName: departureFlight.flight.originAirportName,
    originCode: originCode,
    destination: departureFlight.flight.finalDestination,
    finalDestinationName: departureFlight.flight.finalDestinationName,
    destinationCode: destinationCode,
    total: assembleItemTotal(quotationJourney, departureFlight, returnFlight, fee),
    departureFlight: assembleFlight(
      departureFlight.flight,
      departureFlight.category,
      departureFare,
      isFeatureEnabled,
      false,
      departureFlight.rangePointsPlusMoney
    )
  };

  if (returnFlight) {
    item.returnFlight = assembleFlight(
      returnFlight.flight,
      returnFlight.category,
      returnFare,
      isFeatureEnabled,
      true,
      returnFlight.rangePointsPlusMoney
    );
  }

  if (isFeatureEnabled) {
    item.responsibleCarrier = obtainValidateCarrier(
      departureFlight.flight,
      returnFlight ? returnFlight.flight : null,
      departureFlight.category,
      returnFlight ? returnFlight.category : null
    );
  }

  return item;
}

function assembleBaggageInfo(baggage, category) {
  return _.filter(baggage, ['category', category.toUpperCase()])[0];
}

function assembleFlight(flightData, category, fare, isFeatureEnabled, isReturn, rangePointsPlusMoneyAdt) {
  const flightInfo = {
    id: 1,
    originAirport: flightData.originAirport,
    originAirportName: flightData.originAirportName,
    finalDestination: flightData.finalDestination,
    finalDestinationName: flightData.finalDestinationName,
    category: category.toUpperCase(),
    departureDate: flightData.departureDate,
    departureTime: flightData.departureTime,
    finalArrivalDate: flightData.finalArrivalDate,
    finalArrivalTime: flightData.finalArrivalTime,
    dateVariation: flightData.dateVariation,
    prices: _.filter(flightData.prices, price => price.currency !== Currency.POINTS),
    rangeId: rangePointsPlusMoneyAdt ? rangePointsPlusMoneyAdt.id : null,
    baggage: assembleBaggageInfo(!isReturn ? flightData.baggage : flightData.baggageReturn, category),
    flightGroup: assembleFlightGroup(flightData.flightGroup, fare, isFeatureEnabled)
  };

  if (isFeatureEnabled) {
    flightInfo.fareOriginType = flightData.fareOriginType;
  }

  return flightInfo;
}

function assembleFlightGroup(flightGroupsData, fare, isFeatureEnabled) {
  let flightGroups = flightGroupsData;

  if (fare) {
    flightGroups.forEach(flightGroup => {
      let flightFare = fare[flightGroup.flightNumber.toString()];
      flightGroup.classOfService = flightFare.classOfService;
      flightGroup.fareBasis = flightFare.fareBasis;
      if (isFeatureEnabled) {
        flightGroup.fareType = flightFare.fareTypes;
      }
    });
  }

  return flightGroups;
}

function assembleItemTotal(quotationJourney, departureFlight, returnFlight, fee) {
  let total = [];

  let adultPassengerNumber = quotationJourney.passengers.adultNumber;
  let childPassengerNumber = quotationJourney.passengers.childNumber;
  let infantPassengerNumber = quotationJourney.passengers.infantNumber;

  let totalAdult = departureFlight.value + (returnFlight ? returnFlight.value : 0);
  let totalChild =
    (departureFlight.childValue ? departureFlight.childValue : 0) +
    (returnFlight ? (returnFlight.childValue ? returnFlight.childValue : 0) : 0);
  let totalInfant =
    (departureFlight.infantValue ? departureFlight.infantValue : 0) +
    (returnFlight ? (returnFlight.infantValue ? returnFlight.infantValue : 0) : 0);

  let totalInPoints =
    adultPassengerNumber * totalAdult +
    childPassengerNumber * totalChild +
    infantPassengerNumber * totalInfant;

  total.push({
    value: totalInPoints,
    currency: Currency.POINTS
  });

  if (fee) {
    total.push(fee.total);
  }

  total.push(getTotalMoneyOfPointsPlusMoney(quotationJourney));

  return total;
}

function assembleCartTotal(bookingRequest) {
  const items = bookingRequest.cart.items;
  const prices = items
    .reduce((list, item) => list.concat(item.total), [])
    .reduce((groups, total) => {
      const key = total.currency;
      groups[key] = groups[key] || { currency: total.currency, value: 0 };
      groups[key].value += total.value;
      return groups;
    }, {});

  return [Currency.POINTS, Currency.BRL, Currency.MONEY].map(currency => {
    return prices[currency] || { currency: currency, value: 0 };
  });
}

function assembleRefundRule(quotationJourney, refundRule) {
  if (refundRule === undefined) {
    const { passengers } = quotationJourney;
    return {
      passengerInfo: [
        {
          passengerType: 'ADULT',
          quantity: passengers.adultNumber,
          totalValue: 0,
          refundType: 'NOT_REFUNDABLE'
        },
        {
          passengerType: 'CHILD',
          quantity: passengers.childNumber,
          totalValue: 0,
          refundType: 'NOT_REFUNDABLE'
        },
        {
          passengerType: 'INFANT',
          quantity: passengers.infantNumber,
          totalValue: 0,
          refundType: 'NOT_REFUNDABLE'
        }
      ]
    }
  }
  const passengerInfo = refundRule.passengerInfo;
  return { passengerInfo };
}

function assemblerHeaders(data, isCallcenterMode) {
  var headers = new Headers({
    'Content-Type': 'application/json'
  });
  if (!isCallcenterMode) {
    return headers;
  } else {
    return assemblerHeadersCallcenter(data, headers);
  }
}

function assemblerHeadersCallcenter(data, headers) {
  const adLoginCallcenterStorage = localStorage.getItem(LocalStorage.AD_LOGIN_CALLCENTER);
  const adLoginCallcenter = adLoginCallcenterStorage ? JSON.parse(adLoginCallcenterStorage) : null;
  headers.set('Authorization', adLoginCallcenter && adLoginCallcenter.accessToken ? `Bearer ${adLoginCallcenter.accessToken}` : null);
  headers.set('username', data.username);
  headers.set('customerid', data.customerId);
  headers.set('accountid', data.accountId);
  headers.set('customerdocument', data.document.number);
  headers.set('adlogin', adLoginCallcenter ? adLoginCallcenter.adLogin : null);
  return headers;
}

function getTotalMoneyOfPointsPlusMoney(quotationJourney) {
  const { departureFlight, returnFlight, passengers } = quotationJourney;
  var { rangePointsPlusMoney, childPointsPlusMoney, infantPointsPlusMoney } = departureFlight;
  let total = (rangePointsPlusMoney ? rangePointsPlusMoney.amount * passengers.adultNumber : 0) +
    (childPointsPlusMoney ? childPointsPlusMoney.amount * passengers.childNumber : 0) +
    (infantPointsPlusMoney ? infantPointsPlusMoney.amount * passengers.infantNumber : 0);

  if (returnFlight) {
    var { rangePointsPlusMoney, childPointsPlusMoney, infantPointsPlusMoney } = returnFlight;
    total += (rangePointsPlusMoney ? rangePointsPlusMoney.amount * passengers.adultNumber : 0) +
      (childPointsPlusMoney ? childPointsPlusMoney.amount * passengers.childNumber : 0) +
      (infantPointsPlusMoney ? infantPointsPlusMoney.amount * passengers.infantNumber : 0);
  }
  return {
    value: total,
    currency: Currency.MONEY
  };
}



// WEBPACK FOOTER //
// ./src/services/booking/BookingService.js